import { getApi, get, post, putApi, put, delApi } from "@/shared/utils/fetchApi";
import { TourStatus } from "@/features/Tour/enums/tourStatus";

/**
 * Interface para as configurações do usuário
 */
export interface UserSettings {
  id: string;
  user_id: string;
  language?: string;
  theme?: string;
  metadata?: {
    tour?: {
      step: number;
      status: TourStatus;
    };
  };
  created_at: string;
  updated_at: string;
}

/**
 * Interface para criação de configurações do usuário
 */
/** Upsert do próprio usuário: o `user_id` vem do token, não do body. */
export interface CreateUserSettingsDTO {
  language?: string;
  theme?: string;
}

/**
 * Interface para atualização de configurações do usuário
 */
export interface UpdateUserSettingsDTO {
  language?: string;
  theme?: string;
}

/**
 * Classe de serviço para encapsular todas as operações relacionadas às configurações de usuário
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class UserSettingsService {
  /**
   * A rota é a mesma; o helper muda por método, e a razão é qual rota existe onde.
   *
   * A raiz tem `POST /users/settings` (upsert do self),
   * `GET|PUT /users/settings/user/:userId` e `PUT /users/settings[/tour]` — mas
   * NÃO tem a listagem (`GET /users/settings`), o `GET /users/settings/:id` nem
   * o `PUT /users/settings/:id`. Esses três continuam só sob `/api`, e por isso
   * usam os helpers `getApi`/`putApi`, com o envelope `{data}` que eles devolvem.
   */
  private static readonly BASE_URL = "/users/settings";

  /**
   * Obtém todas as configurações de usuário
   * @returns Lista de configurações de usuário
   */
  static async getAll(): Promise<UserSettings[]> {
    try {
      const result = await getApi<{ data: UserSettings[] }>(this.BASE_URL);
      return result.data;
    } catch (error) {
      console.error("Erro ao buscar configurações de usuário:", error);
      throw error;
    }
  }

  /**
   * Obtém configuração pelo ID
   * @param id ID da configuração
   * @returns Dados da configuração
   */
  static async getById(id: string): Promise<UserSettings> {
    try {
      const result = await getApi<{ data: UserSettings }>(
        `${this.BASE_URL}/${id}`,
      );
      return result.data;
    } catch (error) {
      console.error(`Erro ao buscar configuração com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Obtém configuração pelo ID do usuário
   * @param userId ID do usuário
   * @returns Dados da configuração
   */
  static async getByUserId(userId: string): Promise<UserSettings> {
    try {
      // v2 retorna UserSetting raw; legacy envelopa em { data }

      return await get<UserSettings>(`${this.BASE_URL}/user/${userId}`);
    } catch (error) {
      console.error(
        `Erro ao buscar configuração para usuário ${userId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Cria uma nova configuração de usuário
   * @param data Dados da configuração a ser criada
   * @returns Configuração criada
   */
  static async create(data: CreateUserSettingsDTO): Promise<UserSettings> {
    try {
      // `POST /users/settings` é upsert do próprio usuário (auth.user.id) e
      // responde flat.
      return await post<UserSettings>(this.BASE_URL, {
        language: data.language,
        theme: data.theme,
      });
    } catch (error) {
      console.error("Erro ao criar configuração de usuário:", error);
      throw error;
    }
  }

  /**
   * Atualiza uma configuração existente pelo ID
   * @param id ID da configuração
   * @param data Dados a serem atualizados
   * @returns Configuração atualizada
   */
  static async update(
    id: string,
    data: UpdateUserSettingsDTO,
  ): Promise<UserSettings> {
    try {
      const result = await putApi<{ data: UserSettings }>(
        `${this.BASE_URL}/${id}`,
        data,
      );
      return result.data;
    } catch (error) {
      console.error(`Erro ao atualizar configuração com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Atualiza configuração pelo ID do usuário
   * @param userId ID do usuário
   * @param data Dados a serem atualizados
   * @returns Configuração atualizada
   */
  static async updateByUserId(
    userId: string,
    data: UpdateUserSettingsDTO,
  ): Promise<UserSettings> {
    try {
      // v2: retorna UserSettings flat; legacy: envelope `{data}`.

      return await put<UserSettings>(`${this.BASE_URL}/user/${userId}`, data);
    } catch (error) {
      console.error(
        `Erro ao atualizar configuração para usuário ${userId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Remove uma configuração existente pelo ID
   * @param id ID da configuração
   * @returns Mensagem de sucesso
   */
  static async delete(
    id: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      return await delApi<{ success: boolean; message: string }>(
        `${this.BASE_URL}/${id}`,
      );
    } catch (error) {
      console.error(`Erro ao excluir configuração com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Remove configuração pelo ID do usuário
   * @param userId ID do usuário
   * @returns Mensagem de sucesso
   */
  static async deleteByUserId(
    userId: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      return await delApi<{ success: boolean; message: string }>(
        `${this.BASE_URL}/user/${userId}`,
      );
    } catch (error) {
      console.error(
        `Erro ao excluir configuração para usuário ${userId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Atualiza tour metadata pelo ID do usuário
   * @param userId ID do usuário
   * @param tourData Dados do tour (step e/ou status)
   * @returns Configuração atualizada
   */
  static async updateTourMetadata(
    userId: string,
    tourData: { step?: number; status?: TourStatus },
  ): Promise<UserSettings> {
    try {
      // v2: retorna UserSettings flat; legacy: envelope `{data}`.

      return await put<UserSettings>(
        `${this.BASE_URL}/user/${userId}/tour`,
        tourData,
      );
    } catch (error) {
      console.error(
        `Erro ao atualizar tour metadata para usuário ${userId}:`,
        error,
      );
      throw error;
    }
  }
}
